<template>
  <div :key="reRender" class="relative">
    <div class="p-8">
      <div class="pb-10" v-if="shownError">
        <zt-alert color="danger" filled @afterClosed="shownError = false">
          <template slot="custom-content">
            <div class="flex justify-start items-center p-2">
              <zt-icon
                name="exclamation-circle-solid"
                class="text-2xl mr-4"
              ></zt-icon>
              <p>{{ message }}</p>
            </div>
          </template>
        </zt-alert>
      </div>

      <div class="pb-8">
        <zt-input
          :disabled="!isEditModeActive"
          outlined
          :label="$t('message.documentDetail.commons.cuil')"
          name="cuil"
          v-model="documentModel.cuil"
          v-validate="{ required: true, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
          data-vs-input="cuil"
        />

        <div class="text-zt-danger text-zt-base" v-if="errors.has('cuil')">
          <span>
            {{ errors.first('cuil') }}
          </span>
        </div>
      </div>
      <div class="pb-8">
        <zt-input
          :disabled="true"
          outlined
          :label="$t('message.documentDetail.document.type')"
          v-model="documentModel.tipo_documento"
          data-vs-input="document-type"
        />
      </div>
      <div class="pb-8">
        <zt-input
          v-model="documentModel.periodo"
          :disabled="!isEditModeActive"
          outlined
          label="Periodo"
          readonly
          :key="renderKeyPeriod"
          @click="openCalendar"
          @blur="onBlur"
          data-vs-input="periodo"
        />
        <datepicker
          ref="datepicker"
          v-model="periodCalendar"
          :minimumView="'month'"
          :maximumView="'month'"
          :language="periodLang"
          name="period"
          data-vs-datepicket="period"
          format="MMMM yyyy"
          :bootstrap-styling="true"
          input-class="w-full focus:outline-none border-0 hidden"
          :placeholder="$t('message.documentDetail.document.date')"
          @selected="onSelected"
        >
        </datepicker>
      </div>
      <div class="pb-8">
        <zt-input
          :disabled="!isEditModeActive"
          outlined
          :label="$t('message.documentDetail.document.sector')"
          name="sector"
          v-model="documentModel.sector"
          v-validate="{ required: true, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
          data-vs-input="sector"
        />
        <div class="text-zt-danger text-zt-base" v-if="errors.has('sector')">
          <span>
            {{ errors.first('sector') }}
          </span>
        </div>
      </div>
      <div class="pb-8">
        <zt-input
          :disabled="!isEditModeActive"
          outlined
          :label="$t('message.documentDetail.document.location')"
          name="location"
          v-model="documentModel.locacion"
          v-validate="{ required: true, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
          data-vs-input="location"
        />
        <div class="text-zt-danger text-zt-base" v-if="errors.has('location')">
          <span>
            {{ errors.first('location') }}
          </span>
        </div>
      </div>
      <div class="pb-0">
        <zt-input
          :disabled="true"
          outlined
          :label="$t('message.documentDetail.document.status')"
          v-model="documentModel.estado"
          data-vs-input="estado"
        />
      </div>
      <div class="flex justify-end mt-8">
        <div class="flex">
          <template v-if="!isEditModeActive">
            <div class="pr-2" v-if="isActionAllowed(24)">
              <zt-button @click="changeEditMode" icon="pen" outlined>
                {{ $t('message.documentDetail.document.edit') }}
              </zt-button>
            </div>
            <div class="pl-2" v-if="isActionAllowed(42)">
              <zt-button
                icon="times"
                outlined
                :disabled="isCancelled"
                :loader="isLoading"
                @click="activeCancelDialog(true)"
              >
                {{ $t('message.documentDetail.document.reject') }}
              </zt-button>
            </div>
          </template>
          <template v-else>
            <div class="pr-2">
              <zt-button @click="cancelEditMode" outlined>
                {{ $t('message.documentDetail.document.cancel') }}
              </zt-button>
            </div>
            <div class="pr-2">
              <zt-button
                @click="saveNewInformation"
                :loader="isLoading"
                :disabled="isDisabledSave"
              >
                {{ $t('message.documentDetail.document.save') }}
              </zt-button>
            </div>
          </template>
        </div>
      </div>
    </div>
    <Transition name="fade">
      <div
        v-if="isLoading"
        class="absolute z-10 w-full top-0 left-0 h-full bg-zt-dark-200"
      >
        <div class="w-full h-full">
          <vue-element-loading
            :active="true"
            background-color="#CCCCCC"
            spinner="spinner"
            duration="1.5"
            color="#4f4f4f"
            size="50"
          />
        </div>
      </div>
    </Transition>
    <ConfirmCancelDialog
      :active="shownCancelDialog"
      @handle-click="cancelDocumentInformation"
    />
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import Search from '@/apiClient/Search'
import Folders from '@/apiClient/Folders'
import { mapGetters } from 'vuex'
import Datepicker from 'vuejs-datepicker'
import { es } from 'vuejs-datepicker/dist/locale'
import moment from 'moment'
import { isEqual } from 'lodash'
import ConfirmCancelDialog from '@/modules/Documents/detail/components/modals/ConfirmCancelDialog.vue'
export default {
  name: 'Document',
  inject: ['stateDetailStore', 'setDocumentDetail'],
  provide() {
    return {
      activeCancelDialog: this.activeCancelDialog,
      cancelDocumentInformation: this.cancelDocumentInformation
    }
  },
  components: {
    ConfirmCancelDialog,
    Datepicker,
    VueElementLoading
  },
  data() {
    return {
      isEditModeActive: false,
      isLoading: false,
      documentModel: {
        cuil: '',
        tipo_documento: '',
        periodo: '',
        sector: '',
        locacion: '',
        estado: ''
      },
      reRender: 0,
      renderKeyPeriod: 0,
      periodLang: es,
      periodCalendar: '',
      canSave: true,
      shownCancelDialog: false,
      shownError: false,
      message: this.$t('message.documentDetail.errors.default')
    }
  },
  computed: {
    ...mapGetters(['isActionAllowed']),
    isCancelled() {
      return this.documentModel.estado === 'ANULADO'
    },
    isDisabledSave() {
      return !(!this.isLoading && this.canSave)
    }
  },
  watch: {
    documentModel: {
      handler() {
        const hasChanges = !isEqual(
          this.documentModel,
          this.stateDetailStore.detail.document
        )
        const requiredFields = [
          this.documentModel.cuil,
          this.documentModel.periodo,
          this.documentModel.sector,
          this.documentModel.locacion
        ]
        this.canSave = hasChanges && requiredFields.every((item) => !!item)
      },
      deep: true
    }
  },
  created() {
    this.searchApi = new Search()
    this.apiFolders = new Folders()
    this.documentModel = { ...this.stateDetailStore.detail.document }
  },
  methods: {
    async cancelDocumentInformation() {
      this.shownError = false
      try {
        await this.apiFolders.cancelDocumentInformation(
          this.stateDetailStore.detail.idDocument
        )
        this.documentModel.estado = 'ANULADO'
        await this.updateDocument()
        this.reRender += 1
      } catch (e) {
        if (e.response.data.codes && e.response.data.codes.length) {
          const code = e.response.data.codes[0].code
          this.message = this.$t(`message.documentDetail.errors.${code}`)
        } else {
          this.message = this.$t('message.documentDetail.errors.default')
        }
        this.shownError = true
      }
    },
    async saveNewInformation() {
      if (this.isDisabledSave) return
      this.isLoading = true
      this.shownError = false
      const params = {
        cuil: this.documentModel.cuil,
        locacion: this.documentModel.locacion,
        periodo: moment(this.documentModel.periodo, 'MM/YYYY').format(
          'YYYY-MM'
        ),
        sector: this.documentModel.sector
      }
      try {
        await this.apiFolders.editDocumentInformation(
          this.stateDetailStore.detail.idDocument,
          params
        )
        await this.updateDocument()
        this.reRender += 1
        this.isEditModeActive = false
      } catch (e) {
        if (e.response.data.codes && e.response.data.codes.length) {
          const code = e.response.data.codes[0].code
          this.message = this.$t(`message.documentDetail.errors.${code}`)
        } else {
          this.message = this.$t('message.documentDetail.errors.default')
        }
        this.shownError = true
        this.isEditModeActive = true
      } finally {
        this.isLoading = false
        this.canSave = false
      }
    },
    activeCancelDialog(show) {
      this.shownCancelDialog = show
    },
    cancelEditMode() {
      this.documentModel = { ...this.stateDetailStore.detail.document }
      this.isEditModeActive = false
      this.reRender += 1
    },
    changeEditMode() {
      this.isEditModeActive = !this.isEditModeActive
    },
    openCalendar() {
      this.$refs.datepicker.showCalendar()
    },
    onSelected(date) {
      this.periodCalendar = moment(date).format('MM/YYYY')
      this.$refs.datepicker.close(false)
      this.renderKeyPeriod += 1
      this.documentModel.periodo = this.periodCalendar
    },
    onBlur() {
      this.$refs.datepicker.close(false)
    },
    onClear() {
      this.periodCalendar = ''
      this.$refs.datepicker.selectedDate = null
      this.renderKeyPeriod += 1
      this.statePublish.documentPeriod = ''
    },
    async updateDocument() {
      try {
        const {
          data: { data }
        } = await this.searchApi.getDocumentDetail(
          this.stateDetailStore.detail.idDocument
        )
        this.setDocumentDetail({
          idDocument: this.stateDetailStore.detail.idDocument,
          ...data
        })
        this.documentModel = { ...this.stateDetailStore.detail.document }
        this.isLoading = false
      } catch (error) {
        this.isEmpty = true
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .zt-input-clearable {
  line-height: 1;
  margin-top: 2px;
}

::v-deep .zt-input-text-field label {
  padding: 0 3px;
}
::v-deep .zt-input-disabled,
::v-deep .zt-input-disabled label,
::v-deep .zt-input-disabled input {
  background-color: #f5f5f5;
  color: #969696 !important;
}

::v-deep .zt-input-disabled label {
  color: #969696 !important;
  padding: 0 3px;
}

::v-deep .zt-alert-main-fill-danger .zt-icon {
  margin-top: 4px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
